import { useEffect } from 'react';
import { Theme } from '@radix-ui/themes';
import { resolveThemeMode, useThemeStore } from '../stores/themeStore';

function applyDocumentTheme(resolved: 'light' | 'dark') {
  document.documentElement.style.colorScheme = resolved;
  document.documentElement.dataset.theme = resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const appearance = useThemeStore((state) => state.appearance);
  const resolvedAppearance = useThemeStore((state) => state.resolvedAppearance);
  const setResolvedAppearance = useThemeStore((state) => state.setResolvedAppearance);

  useEffect(() => {
    const resolved = resolveThemeMode(appearance);
    setResolvedAppearance(resolved);
    applyDocumentTheme(resolved);
  }, [appearance, setResolvedAppearance]);

  useEffect(() => {
    if (appearance !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = resolveThemeMode('system');
      setResolvedAppearance(resolved);
      applyDocumentTheme(resolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [appearance, setResolvedAppearance]);

  // Radix `inherit` adds neither .light nor .dark, so its gray/panel tokens fall back to light.
  // Always pass the resolved light/dark value so AI chat Cards/Callouts match the app theme.
  return (
    <Theme
      accentColor="gray"
      appearance={resolvedAppearance}
      scaling="95%"
      style={{ minHeight: '100vh', width: '100%' }}
    >
      {children}
    </Theme>
  );
}
